/**
 * Instant album-art stand-in for Cover Flow while Last.fm / Spotify
 * covers load — or when they never show up (now-playing often has none).
 */

export const MUSIC_SLEEVE_WIDTH = 512;
export const MUSIC_SLEEVE_HEIGHT = 680;

/** Last.fm's default "no album" image hash. */
const LASTFM_MISSING_ART = '2a96cbd8b46e442fc41c2b86b821562f';

const JACKETS = [
  { jacket: '#14532d', jacket2: '#052e16', accent: '#4ade80' },
  { jacket: '#1e1b4b', jacket2: '#312e81', accent: '#f472b6' },
  { jacket: '#7c2d12', jacket2: '#1c1917', accent: '#fbbf24' },
  { jacket: '#0c4a6e', jacket2: '#082f49', accent: '#38bdf8' },
  { jacket: '#4c0519', jacket2: '#1f0a13', accent: '#fb7185' },
  { jacket: '#2e1065', jacket2: '#1e1b4b', accent: '#c4b5fd' },
  { jacket: '#134e4a', jacket2: '#042f2e', accent: '#5eead4' },
  { jacket: '#431407', jacket2: '#0c0a09', accent: '#fb923c' },
] as const;

const STICKERS = ['SIDE A', '45 RPM', 'BOOTLEG', 'B-SIDE', 'DEEP CUT', 'LOUD'];

export type MusicSleeveDrawOpts = {
  title: string;
  artist?: string;
  nowPlaying?: boolean;
  beat?: number;
};

export function isUsableAlbumArtUrl(url?: string | null): url is string {
  if (!url || !url.trim()) return false;
  return !url.includes(LASTFM_MISSING_ART);
}

export function hashString(input: string): number {
  let hash = 2166136261;
  for (let i = 0; i < input.length; i++) {
    hash ^= input.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return hash >>> 0;
}

export function sleeveInitials(title: string, artist?: string): string {
  const source =
    artist && artist.trim().split(/\s+/).length <= 3 ? artist : title;
  const words = source
    .trim()
    .split(/\s+/)
    .filter((word) => word.length > 0 && !/^the$/i.test(word));
  if (words.length >= 2) {
    return `${words[0][0]}${words[1][0]}`.toUpperCase();
  }
  const compact = source.replace(/[^a-zA-Z0-9]/g, '');
  return (compact.slice(0, 2) || 'LP').toUpperCase();
}

function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
) {
  const radius = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  if (typeof ctx.roundRect === 'function') {
    ctx.roundRect(x, y, w, h, radius);
  } else {
    ctx.rect(x, y, w, h);
  }
}

export function drawMusicSleeve(
  ctx: CanvasRenderingContext2D,
  opts: MusicSleeveDrawOpts,
  width = MUSIC_SLEEVE_WIDTH,
  height = MUSIC_SLEEVE_HEIGHT,
) {
  const { title, artist = '', nowPlaying = false, beat = 0 } = opts;
  const seed = hashString(`${artist}\0${title}`);
  const pal = JACKETS[seed % JACKETS.length];
  const sticker = STICKERS[seed % STICKERS.length];

  const jacket = ctx.createLinearGradient(0, 0, width, height);
  jacket.addColorStop(0, pal.jacket);
  jacket.addColorStop(1, pal.jacket2);
  ctx.fillStyle = jacket;
  ctx.fillRect(0, 0, width, height);

  ctx.fillStyle = pal.accent;
  ctx.fillRect(0, 0, 18, height);

  ctx.fillStyle = 'rgba(255,255,255,0.07)';
  for (let i = 0; i < 90; i++) {
    const x = (seed + i * 97) % width;
    const y = (seed * 3 + i * 53) % height;
    ctx.fillRect(x, y, 2, 2);
  }

  const cx = width / 2 + 8;
  const cy = height * 0.4;
  const radius = Math.min(width, height) * 0.34;

  ctx.beginPath();
  ctx.arc(cx, cy, radius, 0, Math.PI * 2);
  ctx.fillStyle = '#0a0a0a';
  ctx.fill();

  ctx.strokeStyle = 'rgba(255,255,255,0.07)';
  ctx.lineWidth = 1.25;
  for (let i = 7; i < 28; i++) {
    ctx.beginPath();
    ctx.arc(cx, cy, radius * (i / 30), 0, Math.PI * 2);
    ctx.stroke();
  }

  ctx.beginPath();
  ctx.arc(cx, cy, radius * 0.97, -0.7, 0.45);
  ctx.strokeStyle = 'rgba(255,255,255,0.2)';
  ctx.lineWidth = 7;
  ctx.stroke();

  const labelR = radius * 0.34;
  ctx.beginPath();
  ctx.arc(cx, cy, labelR, 0, Math.PI * 2);
  ctx.fillStyle = pal.accent;
  ctx.fill();

  ctx.beginPath();
  ctx.arc(cx, cy, 11, 0, Math.PI * 2);
  ctx.fillStyle = '#111827';
  ctx.fill();

  ctx.fillStyle = '#0a0a0a';
  ctx.font = 'bold 40px ui-sans-serif, system-ui, sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(sleeveInitials(title, artist), cx, cy);

  ctx.save();
  ctx.translate(width * 0.22, height * 0.14);
  ctx.rotate(-0.22);
  roundRect(ctx, -46, -15, 92, 30, 6);
  ctx.fillStyle = '#fafaf9';
  ctx.fill();
  ctx.fillStyle = '#0a0a0a';
  ctx.font = 'bold 13px ui-sans-serif, system-ui, sans-serif';
  ctx.fillText(sticker, 0, 1);
  ctx.restore();

  if (nowPlaying) {
    ctx.save();
    ctx.translate(width * 0.8, height * 0.13);
    ctx.rotate(0.16);
    roundRect(ctx, -50, -15, 100, 30, 6);
    ctx.fillStyle = '#22c55e';
    ctx.fill();
    ctx.fillStyle = '#052e16';
    ctx.font = 'bold 13px ui-sans-serif, system-ui, sans-serif';
    ctx.fillText('ON AIR', 0, 1);
    ctx.restore();
  }

  const barCount = 9;
  const barW = 18;
  const gap = 10;
  const totalW = barCount * barW + (barCount - 1) * gap;
  let x0 = (width - totalW) / 2;
  const baseY = height - 44;
  const maxH = 70;
  ctx.fillStyle = pal.accent;
  for (let i = 0; i < barCount; i++) {
    const phase = beat * 6 + i * 0.7 + (seed % 11) * 0.15;
    const h = 14 + (0.35 + 0.65 * (0.5 + 0.5 * Math.sin(phase))) * maxH;
    roundRect(ctx, x0, baseY - h, barW, h, 4);
    ctx.fill();
    x0 += barW + gap;
  }

  ctx.fillStyle = pal.accent;
  ctx.font = '28px Georgia, serif';
  ctx.textAlign = 'left';
  const noteY = height * 0.76 + Math.sin(beat * 3) * 5;
  ctx.fillText('♪', width * 0.14, noteY);
  ctx.fillText('♫', width * 0.82, noteY - 10);
}
